import * as classNames from 'classnames';
import * as React from 'react';
import withStyles, { StyledComponentProps } from 'react-jss';
import { RouteComponentProps, withRouter } from 'react-router';
import { InjectedCursorProps, withCursor } from 'src/_context/Cursor';
import Responsive from 'src/_context/Responsive';
import responsive from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { directions } from 'src/constants/stylesheet';
import Image from './Image';
import { Slider, SliderState } from './Slider';
import CurrentIndex from './Slider/CurrentIndex';
import CurrentLegend from './Slider/CurrentLegend';
import SliderRail, { RailZone } from './SliderRail';

export type HomeProps = HomeDto;

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  header: {
    display: 'flex',
    flex: '0 0 auto',
    ...stylesheet.MarginGutter(.5, directions.sides | directions.top)
  },
  headerTitle: {
    [responsive.Mobile]: {
      width: spacing.MainLeftSideWidth.Mobile,
      margin: `${spacing.Gutter.Mobile / 2}px ${spacing.Gutter.Mobile / 2}px 0 ${spacing.Gutter.Mobile / 2}px`
    },
    [responsive.Tablet]: {
      width: spacing.MainLeftSideWidth.Tablet,
      margin: `${spacing.Gutter.Tablet / 2}px ${spacing.Gutter.Tablet / 2}px 0 ${spacing.Gutter.Tablet / 2}px`
    },
    [responsive.Laptop]: {
      width: spacing.MainLeftSideWidth.Laptop,
      margin: `${spacing.Gutter.Laptop / 2}px ${spacing.Gutter.Laptop / 2}px 0 ${spacing.Gutter.Laptop / 2}px`
    },
    [responsive.Desktop]: {
      width: spacing.MainLeftSideWidth.Desktop,
      margin: `${spacing.Gutter.Desktop / 2}px ${spacing.Gutter.Desktop / 2}px 0 ${spacing.Gutter.Desktop / 2}px`
    }
  },
  headerCounter: {
    [responsive.Mobile]: {
      width: spacing.MainRightSideWidth.Mobile,
      margin: `${spacing.Gutter.Mobile / 2}px ${spacing.Gutter.Mobile / 2}px 0 ${spacing.Gutter.Mobile / 2}px`
    },
    [responsive.Tablet]: {
      width: spacing.MainRightSideWidth.Tablet,
      margin: `${spacing.Gutter.Tablet / 2}px ${spacing.Gutter.Tablet / 2}px 0 ${spacing.Gutter.Tablet / 2}px`
    },
    [responsive.Laptop]: {
      width: spacing.MainRightSideWidth.Laptop,
      margin: `${spacing.Gutter.Laptop / 2}px ${spacing.Gutter.Laptop / 2}px 0 ${spacing.Gutter.Laptop / 2}px`
    },
    [responsive.Desktop]: {
      width: spacing.MainRightSideWidth.Desktop,
      margin: `${spacing.Gutter.Desktop / 2}px ${spacing.Gutter.Desktop / 2}px 0 ${spacing.Gutter.Desktop / 2}px`
    }
  },
  headerMovile: {
    marginTop: 0,
    // marginBottom: spacing.Gutter.Mobile / 2
  },
  headerContentMobile: {
    ...stylesheet.MarginGutter(1, directions.sides | directions.bottom)
  },
  title: {
    color: 'black'
  },
  subTitle: {},
  sliderContainer: {
    position: 'relative',
    flex: '1 1 auto',
    ...stylesheet.MarginGutter(1, directions.bottom | directions.top),
    overflow: 'hidden'
  },
  imageContainer: {
    height: '100%',
    [responsive.Mobile]: {
      margin: `0 ${spacing.Gutter.Mobile}px`
    },
    [responsive.Tablet]: {
      margin: `0 ${spacing.Gutter.Tablet}px`
    },
    [responsive.Laptop]: {
      margin: `0 ${spacing.Gutter.Laptop * 2 + spacing.Logo.Laptop}px 0 ${spacing.Gutter.Laptop}px`
    },
    [responsive.Desktop]: {
      margin: `0 ${spacing.Gutter.Desktop * 2 + spacing.Logo.Laptop}px 0 ${spacing.Gutter.Desktop}px`
    }
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'bottom left',
    pointerEvents: 'none',
    [responsive.Mobile]: {
      objectPosition: 'top left'
    },
    [responsive.AfterMobile]: {
      objectPosition: 'bottom left'
    }
  },
  slideNext: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '50%',
    right: 0
  },
  slidePrevios: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '50%',
    left: 0
  }
}

type Props = HomeProps &
  InjectedCursorProps &
  RouteComponentProps<any> &
  StyledComponentProps<keyof typeof styles>;

class Home extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this._hover = this._hover.bind(this);
    this._cursorDefault = this._cursorDefault.bind(this);
  }
  public render() {

    const { classes } = this.props;

    const pageDto = this.props as HomeDto;
    const slides = pageDto.Slides.map((s, i) =>
      <div key={i} className={classes!.imageContainer}>
        <Image
          className={classes!.image}
          pictureRef={s.Image} sizes="100vw"
        />
      </div>);

    const titles = pageDto.Slides.map((s, i) => <span key={i}>{s.Title}</span>);
    const locations = pageDto.Slides.map((s, i) => <span key={i}>{s.Location}</span>);
    const links = pageDto.Slides.map((s, i) => <a href={s.Link} key={i}>{s.LinkText}</a>);
    const paths = pageDto.Slides.map(s => s.Link);

    return (
      <div className={classes!.container}>
        <Slider width={100} nodes={slides} delay={6000} loop={true}>
          <div className={classes!.header}>
            <div className={classes!.headerTitle}>
              <h2 className={classes!.title}>
                <CurrentLegend legends={titles} />
              </h2>
              <h3 className={classes!.subTitle}>
                <CurrentLegend legends={locations} />
              </h3>
            </div>
            <Responsive.Consumer steps={['AfterMobile']}>
              <div className={classNames(classes!.headerCounter)}>
                <div className={classes!.title}><CurrentIndex />{` — ${pageDto.Slides.length}`}</div>
                <nav className={classes!.subTitle}><CurrentLegend legends={links} /></nav>
              </div>
            </Responsive.Consumer>
          </div>
          <div
            className={classes!.sliderContainer}
          >
            <Slider.Consumer>
              {(state: SliderState) => {
                const { current } = state;
                const click = (zone: RailZone) => this._click(zone, state);
                return (
                  <SliderRail
                    leave={this._cursorDefault}
                    hasCenter={!!paths[current]}
                    click={click}
                    hover={this._hover}
                  />);
              }}
            </Slider.Consumer>
          </div>
          <Responsive.Consumer steps={['Mobile']}>
            <div className={classNames(classes!.headerMovile)}>
              <div className={classes!.headerContentMobile}>
                <div className={classes!.title}><CurrentIndex />{` — ${pageDto.Slides.length}`}</div>
                <nav className={classes!.subTitle}><CurrentLegend legends={links} /></nav>
              </div>
            </div>
          </Responsive.Consumer>
        </Slider>
      </div>
    );
  }

  private _click(zone: RailZone, state: SliderState) {
    const { goNext, goPrevious, current } = state;
    const pageDto = this.props as HomeDto;
    const paths = pageDto.Slides.map(s => s.Link);
    const { history: { push } } = this.props;

    switch (zone) {
      case 'center':
        push(paths[current]);
        break;
      case 'previous':
        goPrevious();
        break;
      case 'next':
        goNext();
        break;
    }
  }

  private _hover(zone: RailZone) {
    switch (zone) {
      case 'center':
        this.props.cursor.setType('link')
        break;
      case 'previous':
        this.props.cursor.setType('previous')
        break;
      case 'next':
        this.props.cursor.setType('next');
        break;
      default:
        this.props.cursor.setType('default');
        break;
    }
  }

  private _cursorDefault() {
    const { cursor: { setType } } = this.props;
    setType('default');
  }
  // private _cursorNext() {
  //   this.props.cursor.setType('next')
  // }

  // private _cursorPrevious() {
  //   this.props.cursor.setType('previous')
  // }
}

export default withStyles(styles)(withRouter(withCursor(Home)));



// WEBPACK FOOTER //
// ./src/components/Home.tsx